import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, GitBranch, Trophy, Shuffle, ListOrdered, RefreshCw, Crown, ChevronUp, ChevronDown, Check, Lock, LockOpen, Download, MapPin } from "lucide-react";
import { jsPDF } from "jspdf";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { FORMATS, categoryLabel, fmtDate, shortTeam } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const BRACKET_LABEL = { W: "Winners Bracket", L: "Losers Bracket", F: "Grand Final", P3: "3rd Place", RR: "Round Robin" };
const isReal = (x) => x && x !== "BYE";

function MatchCard({ m, onPick, readOnly, courts = [], onSetCourt }) {
  const canPick = !readOnly && isReal(m.a_name) && isReal(m.b_name) && !m.winner;
  const hasScore = (m.a_score || 0) + (m.b_score || 0) > 0;
  const side = (slot, id, name, score) => {
    const isWinner = m.winner && m.winner === id;
    const clickable = canPick;
    return (
      <button
        data-testid={`match-${m.match_id}-${slot}`}
        disabled={!clickable}
        onClick={() => clickable && onPick(m.match_id, id)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors disabled:cursor-default"
        style={{ background: isWinner ? "rgba(34,197,94,0.14)" : "transparent" }}>
        <span className="text-xs truncate" style={{ color: name && name !== "BYE" ? "var(--text)" : "var(--text-2)", fontWeight: isWinner ? 700 : 500 }} title={name || ""}>
          {name ? shortTeam(name) : "TBD"}
        </span>
        <span className="flex items-center gap-1.5 shrink-0 ml-2">
          {hasScore && <span data-testid={`match-${m.match_id}-${slot}-score`} className="mono text-xs font-bold" style={{ color: isWinner ? "#22c55e" : (m.live ? "var(--primary)" : "var(--text-2)") }}>{score ?? 0}</span>}
          {isWinner && <Check className="w-3.5 h-3.5 shrink-0" style={{ color: "#22c55e" }} />}
        </span>
      </button>
    );
  };
  const showCourtPicker = !readOnly && onSetCourt && courts.length > 0;
  return (
    <div data-testid={`match-${m.match_id}`} className="rounded-lg glow-border overflow-hidden shrink-0" style={{ background: "var(--surface)", width: 168, boxShadow: m.live ? "0 0 14px rgba(0,229,255,0.22)" : "none" }}>
      {m.live && (
        <div className="px-3 py-0.5 flex items-center gap-1.5 text-[9px] mono uppercase tracking-wider" style={{ background: "var(--bg)", color: "var(--primary-ink)" }} data-testid={`match-live-${m.match_id}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
        </div>
      )}
      {side("a", m.a, m.a_name, m.a_score)}
      <div style={{ height: 1, background: "var(--border)" }} />
      {side("b", m.b, m.b_name, m.b_score)}
      {showCourtPicker ? (
        <div className="px-2 py-1.5 border-t" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
          <select
            data-testid={`match-court-${m.match_id}`}
            value={m.court_name || ""}
            onChange={(e) => onSetCourt(m.match_id, e.target.value)}
            className="w-full rounded bg-surface glow-border px-1.5 py-1 text-[10px] mono outline-none"
            style={{ color: m.court_name ? "var(--primary)" : "var(--text-2)" }}>
            <option value="">No court</option>
            {courts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      ) : m.court_name ? (
        <div data-testid={`match-court-label-${m.match_id}`} className="px-3 py-1 border-t text-[10px] mono uppercase tracking-wider flex items-center gap-1" style={{ borderColor: "var(--border)", background: "var(--bg)", color: "var(--primary-ink)" }}>
          <MapPin className="w-2.5 h-2.5" /> {m.court_name}
        </div>
      ) : null}
    </div>
  );
}

function Column({ title, matches, onPick, readOnly, courts, onSetCourt }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{title}</div>
      <div className="flex flex-col gap-3 justify-around flex-1">
        {matches.map((m) => <MatchCard key={m.match_id} m={m} onPick={onPick} readOnly={readOnly} courts={courts} onSetCourt={onSetCourt} />)}
      </div>
    </div>
  );
}

function EliminationView({ view, onPick, readOnly, courts, onSetCourt }) {
  const groups = ["W", "L", "F", "P3"].filter((g) => view.matches.some((m) => m.bracket === g));
  return (
    <div className="space-y-6" data-testid="bracket-elim">
      {groups.map((g) => {
        const gm = view.matches.filter((m) => m.bracket === g);
        const rounds = [...new Set(gm.map((m) => m.round))].sort((a, b) => a - b);
        return (
          <div key={g}>
            {(g === "L" || g === "F" || g === "P3") && <div className="text-xs font-bold uppercase tracking-wider mb-2 text-primary-c">{BRACKET_LABEL[g]}</div>}
            <div className="flex gap-6 overflow-x-auto pb-2">
              {rounds.map((r) => {
                const rms = gm.filter((m) => m.round === r).sort((a, b) => a.slot - b.slot);
                return (
                  <Column key={r} title={rms[0]?.round_label || `Round ${r}`}
                    matches={rms} onPick={onPick} readOnly={readOnly} courts={courts} onSetCourt={onSetCourt} />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TeamSchedules({ matches }) {
  const [open, setOpen] = useState(false);
  const teams = {};
  matches.forEach((m) => [["a", "b"], ["b", "a"]].forEach(([me, op]) => {
    const id = m[me];
    if (!id) return;
    (teams[id] = teams[id] || { name: m[`${me}_name`], games: [] }).games.push({ round: m.round, label: m.round_label, opp: m[`${op}_name`], res: m.winner ? (m.winner === id ? "W" : "L") : null });
  }));
  const list = Object.entries(teams).sort((x, y) => (x[1].name || "").localeCompare(y[1].name || ""));
  const counts = [...new Set(list.map(([, t]) => t.games.length))];
  return (
    <div className="rounded-xl glass p-3" data-testid="rr-team-schedules">
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="font-bold" data-testid="rr-games-per-team">Games per team: {counts.length === 1 ? counts[0] : counts.sort((a, b) => a - b).join("–")}</span>
        <span className="text-xs" style={{ color: "var(--text-2)" }}>{list.length} teams · {matches.length} matches · every team plays every other team once</span>
        <button data-testid="rr-toggle-team-schedules" onClick={() => setOpen((o) => !o)} className="ml-auto text-xs font-bold text-primary-c">{open ? "Hide" : "Show"} per-team schedule</button>
      </div>
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {list.map(([id, t]) => (
            <div key={id} className="rounded-lg p-2.5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid={`rr-team-${id}`}>
              <div className="text-sm font-bold flex justify-between"><span className="truncate">{t.name}</span><span className="mono text-xs">{t.games.length} games</span></div>
              {t.games.sort((a, b) => a.round - b.round).map((g, i) => (
                <div key={i} className="text-xs flex gap-2 mt-1" style={{ color: "var(--text-2)" }}>
                  <span className="mono w-8">R{g.round}</span><span className="flex-1 truncate">vs {g.opp}</span>
                  {g.res && <span className="mono font-bold" style={{ color: g.res === "W" ? "#22c55e" : "#ef4444" }}>{g.res}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RoundRobinView({ view, onPick, readOnly, courts, onSetCourt }) {
  const rounds = [...new Set(view.matches.map((m) => m.round))].sort((a, b) => a - b);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-5" data-testid="bracket-rr">
      <div className="space-y-4">
        <TeamSchedules matches={view.matches} />
        {rounds.map((r) => (
          <div key={r}>
            <div className="text-[10px] mono uppercase tracking-wider mb-2" style={{ color: "var(--text-2)" }}>{view.matches.find((m) => m.round === r)?.round_label || `Round ${r}`}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {view.matches.filter((m) => m.round === r).map((m) => <MatchCard key={m.match_id} m={m} onPick={onPick} readOnly={readOnly} courts={courts} onSetCourt={onSetCourt} />)}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl glow-border p-4 h-fit" style={{ background: "var(--surface)" }} data-testid="rr-standings">
        <div className="font-display font-bold text-sm mb-3 flex items-center gap-2"><ListOrdered className="w-4 h-4 text-primary-c" /> Standings</div>
        <div className="space-y-1.5">
          {(view.standings || []).map((s, i) => (
            <div key={s.entrant_id} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 truncate"><span className="mono text-primary-c w-5">#{i + 1}</span>{s.name}</span>
              <span className="mono font-bold">{s.wins}W</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Bracket({ tournament, category }) {
  const tid = tournament.tournament_id;
  const cid = category.id;
  const [view, setView] = useState(undefined); // undefined loading | null none | object
  const [entrants, setEntrants] = useState([]);
  const [seeding, setSeeding] = useState("random");
  const [consolation, setConsolation] = useState(false);
  const [order, setOrder] = useState([]);
  const [busy, setBusy] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [advN, setAdvN] = useState(1);
  const [playoff3rd, setPlayoff3rd] = useState(false);
  const mutatingRef = useRef(false);
  const bracketRef = useRef(null);

  const loadEntrants = useCallback(() => {
    api.get(`/tournaments/${tid}/categories/${cid}/entrants`).then(({ data }) => {
      setEntrants(data);
      setOrder((prev) => {
        const prevIds = prev.map((e) => e.registration_id).sort().join(",");
        const newIds = data.map((e) => e.registration_id).sort().join(",");
        return prevIds === newIds ? prev : data; // preserve manual ordering unless roster changed
      });
    }).catch(() => setEntrants([]));
  }, [tid, cid]);

  const load = useCallback(() => {
    setView(undefined);
    api.get(`/tournaments/${tid}/categories/${cid}/bracket`).then(({ data }) => setView(data)).catch((e) => {
      if (e.response?.status === 404) { setView(null); loadEntrants(); } else setView(null);
    });
  }, [tid, cid, loadEntrants]);

  // silent poll: keep the bracket in sync with umpire-submitted results without a spinner
  const refresh = useCallback(() => {
    if (mutatingRef.current) return;
    api.get(`/tournaments/${tid}/categories/${cid}/bracket`)
      .then(({ data }) => setView((v) => (v && typeof v === "object" ? data : v)))
      .catch(() => {});
  }, [tid, cid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [refresh]);
  // when no bracket exists yet, keep the entrant list live so newly confirmed roster players appear without a page refresh
  useEffect(() => {
    if (view !== null) return;
    const t = setInterval(loadEntrants, 2500);
    return () => clearInterval(t);
  }, [view, loadEntrants]);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { seeding, consolation: category.format === "single_elim" ? consolation : false };
      if (seeding === "manual") body.seed_order = order.map((e) => e.registration_id);
      const { data } = await api.post(`/tournaments/${tid}/categories/${cid}/bracket`, body);
      setView(data);
      toast.success("Bracket generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate"); }
    finally { setBusy(false); }
  };

  const pick = async (matchId, entrantId) => {
    mutatingRef.current = true;
    try {
      const { data } = await api.post(`/tournaments/${tid}/categories/${cid}/bracket/match/${matchId}/winner`, { winner_entrant_id: entrantId });
      setView(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not record result"); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const setCourt = async (matchId, courtName) => {
    mutatingRef.current = true;
    try {
      const { data } = await api.post(`/tournaments/${tid}/categories/${cid}/bracket/match/${matchId}/court`, { court_name: courtName });
      setView(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not set court"); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const regenerate = async () => {
    setRegenOpen(false);
    try { await api.delete(`/tournaments/${tid}/categories/${cid}/bracket`); setView(null); loadEntrants(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not reset"); }
  };

  const toggleLock = async () => {
    mutatingRef.current = true;
    try {
      const { data } = await api.post(`/tournaments/${tid}/categories/${cid}/bracket/lock`, { locked: !view.locked });
      setView(data);
      toast.success(data.locked ? "Bracket locked — seeding is final" : "Bracket unlocked");
    } catch (e) { toast.error("Could not update lock"); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const genPlayoffs = async () => {
    if (view.has_playoff && !window.confirm("Rebuild playoffs? Current playoff results will be cleared.")) return;
    mutatingRef.current = true;
    try {
      const { data } = await api.post(`/tournaments/${tid}/categories/${cid}/bracket/playoffs`, { advance_per_pool: advN, consolation: playoff3rd });
      setView(data);
      toast.success("Playoffs generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate playoffs"); }
    finally { setTimeout(() => { mutatingRef.current = false; }, 800); }
  };

  const downloadPdf = () => {
    if (!view || typeof view !== "object") return;
    setBusy(true);
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const PW = pdf.internal.pageSize.getWidth();
      const PH = pdf.internal.pageSize.getHeight();
      const M = 42;
      const BOTTOM = PH - 46;
      let y = 0;

      const ensure = (h) => { if (y + h > BOTTOM) { pdf.addPage(); y = M; } };

      const drawHeader = () => {
        pdf.setFillColor(10, 15, 29);
        pdf.rect(0, 0, PW, 96, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(20);
        pdf.text(tournament.name || "Tournament", M, 40, { maxWidth: PW - M * 2 });
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(13);
        pdf.setTextColor(56, 189, 248);
        pdf.text(categoryLabel(category), M, 62);
        pdf.setTextColor(200, 208, 220);
        pdf.setFontSize(9);
        const meta = [
          FORMATS.find((f) => f.v === category.format)?.l,
          category.pools > 1 ? `${category.pools} pools` : null,
          tournament.venue,
          [fmtDate(tournament.start_date), fmtDate(tournament.end_date)].filter(Boolean).join(" – "),
        ].filter(Boolean).join("     •     ");
        pdf.text(meta, M, 80, { maxWidth: PW - M * 2 });
        pdf.setTextColor(30, 30, 30);
        y = 118;
      };

      const sectionTitle = (str) => {
        ensure(34);
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(6, 150, 137);
        pdf.text(str, M, y); y += 7;
        pdf.setDrawColor(6, 150, 137); pdf.setLineWidth(1.2); pdf.line(M, y, PW - M, y);
        y += 18; pdf.setTextColor(30, 30, 30);
      };

      const subTitle = (str) => {
        ensure(22);
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(10.5); pdf.setTextColor(90, 96, 110);
        pdf.text(str, M, y); y += 16; pdf.setTextColor(30, 30, 30);
      };

      const twoCol = (left, right, bold) => {
        ensure(15);
        pdf.setFont("helvetica", bold ? "bold" : "normal"); pdf.setFontSize(10);
        pdf.text(left, M + 6, y);
        if (right) { pdf.setTextColor(120, 124, 134); pdf.text(right, PW - M, y, { align: "right" }); pdf.setTextColor(30, 30, 30); }
        y += 15;
      };

      const matchGroup = (matches) => {
        const rounds = [...new Set(matches.map((m) => m.round))].sort((a, b) => a - b);
        rounds.forEach((r) => {
          const rms = matches.filter((m) => m.round === r).sort((a, b) => (a.slot || 0) - (b.slot || 0));
          subTitle(rms[0]?.round_label || `Round ${r}`);
          rms.forEach((m, i) => {
            const a = m.a_name || "TBD", b = m.b_name || "TBD";
            const extras = [];
            if (m.winner) extras.push(`Winner: ${m.winner === m.a ? a : (m.winner === m.b ? b : (m.winner_name || ""))}`);
            if (m.court_name) extras.push(m.court_name);
            twoCol(`${i + 1}.  ${a}   vs   ${b}`, extras.join("   ·   "));
          });
          y += 8;
        });
      };

      const standings = (rows, title) => {
        if (!rows || !rows.length) return;
        subTitle(title || "Standings");
        rows.forEach((s, i) => twoCol(`#${i + 1}   ${s.name}`, `${s.wins}W`));
        y += 8;
      };

      drawHeader();

      if (view.champion_name) {
        sectionTitle("Champion");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(14); pdf.setTextColor(202, 138, 4);
        ensure(20); pdf.text(view.champion_name, M + 6, y); y += 24;
        pdf.setTextColor(30, 30, 30);
      }

      sectionTitle(`Seeding  (${(view.entrants || []).length} entrants)`);
      (view.entrants || []).forEach((e, i) => twoCol(`#${e.seed || i + 1}   ${e.name}`));
      y += 6;

      if (view.pool_count > 1) {
        (view.pools || []).forEach((p) => {
          sectionTitle(p.label + (p.champion_name ? `  —  Winner: ${p.champion_name}` : ""));
          matchGroup(view.matches.filter((m) => m.pool === p.pool_index && m.stage !== "playoff"));
          standings(p.standings, `${p.label} — Standings`);
        });
        const playoffs = view.matches.filter((m) => m.stage === "playoff");
        if (playoffs.length) { sectionTitle("Playoffs — Overall Title"); matchGroup(playoffs); }
      } else {
        sectionTitle("Matches");
        matchGroup(view.matches);
        if (view.format === "round_robin") standings(view.standings);
      }

      const pages = pdf.internal.getNumberOfPages();
      const stamp = new Date().toLocaleString();
      for (let i = 1; i <= pages; i++) {
        pdf.setPage(i);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(150, 154, 164);
        pdf.text(`${tournament.name} · ${categoryLabel(category)}`, M, PH - 26, { maxWidth: PW - M * 2 - 90 });
        pdf.text(`Page ${i} of ${pages}`, PW - M, PH - 26, { align: "right" });
        pdf.text(`Generated ${stamp} · PointControl`, M, PH - 14);
      }

      pdf.save(`${tournament.name}-${categoryLabel(category)}-bracket.pdf`.replace(/[^a-z0-9.\-]+/gi, "_"));
      toast.success("Bracket PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setBusy(false); }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const arr = [...order];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setOrder(arr);
  };

  if (view === undefined) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>;

  // no bracket yet -> generation panel
  if (view === null) {
    return (
      <div data-testid="bracket-generate">
        <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>
          <span className="font-bold text-primary-c">{entrants.length}</span> confirmed entrant(s). {FORMATS.find((f) => f.v === category.format)?.l}. {entrants.length < 2 && "Confirm at least 2 registrations to generate."}
        </p>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="glass rounded-full p-0.5 flex gap-0.5">
            <button data-testid="seed-random" onClick={() => setSeeding("random")} className="rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={seeding === "random" ? { background: "var(--primary)", color: "var(--on-primary)" } : { color: "var(--text-2)" }}><Shuffle className="w-3.5 h-3.5" /> Random</button>
            <button data-testid="seed-manual" onClick={() => setSeeding("manual")} className="rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={seeding === "manual" ? { background: "var(--primary)", color: "var(--on-primary)" } : { color: "var(--text-2)" }}><ListOrdered className="w-3.5 h-3.5" /> Manual seed</button>
          </div>
          {category.format === "single_elim" && (
            <label className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
              <Switch data-testid="consolation-toggle" checked={consolation} onCheckedChange={setConsolation} /> 3rd-place match
            </label>
          )}
        </div>
        {seeding === "manual" && (
          <div className="rounded-xl p-3 mb-4 space-y-1.5 max-h-64 overflow-y-auto" style={{ background: "var(--bg)" }} data-testid="seed-order-list">
            {order.map((e, i) => (
              <div key={e.registration_id} className="flex items-center justify-between glass rounded-lg px-3 py-1.5">
                <span className="text-sm truncate"><span className="mono text-primary-c mr-2">#{i + 1}</span>{e.name}</span>
                <div className="flex gap-1">
                  <button data-testid={`seed-up-${i}`} onClick={() => move(i, -1)} className="w-6 h-6 rounded glass flex items-center justify-center"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button data-testid={`seed-down-${i}`} onClick={() => move(i, 1)} className="w-6 h-6 rounded glass flex items-center justify-center"><ChevronDown className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button data-testid="generate-bracket" onClick={generate} disabled={busy || entrants.length < 2}
          className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitBranch className="w-4 h-4" />} Generate bracket
        </button>
      </div>
    );
  }

  // bracket exists
  const courts = tournament.courts || [];
  const renderBody = (v) => v.format === "round_robin"
    ? <RoundRobinView view={v} onPick={pick} courts={courts} onSetCourt={setCourt} />
    : <EliminationView view={v} onPick={pick} courts={courts} onSetCourt={setCourt} />;

  return (
    <div data-testid="bracket-view">
      {view.champion_name && (
        <div className="rounded-xl glow-border p-4 mb-5 flex items-center gap-3" style={{ background: "rgba(34,197,94,0.10)" }} data-testid="bracket-champion">
          <Crown className="w-6 h-6" style={{ color: "#eab308" }} />
          <div><div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Champion</div><div className="font-display font-bold text-lg">{view.champion_name}</div></div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {view.locked && <span data-testid="bracket-locked-badge" className="text-[10px] mono uppercase tracking-wider glass rounded-full px-2.5 py-1 flex items-center gap-1.5" style={{ color: "#eab308" }}><Lock className="w-3 h-3" /> Locked</span>}
          {view.pool_count > 1 && <span className="text-[10px] mono uppercase tracking-wider glass rounded-full px-2.5 py-1" style={{ color: "var(--text-2)" }}>{view.pool_count} pools</span>}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="download-bracket-pdf" onClick={downloadPdf} disabled={busy} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" style={{ color: "var(--text)" }}><Download className="w-3.5 h-3.5 text-primary-c" /> PDF</button>
          <button data-testid="lock-bracket" onClick={toggleLock} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}>
            {view.locked ? <><LockOpen className="w-3.5 h-3.5 text-primary-c" /> Unlock</> : <><Lock className="w-3.5 h-3.5 text-primary-c" /> Lock</>}
          </button>
          <button data-testid="regenerate-bracket" onClick={() => setRegenOpen(true)} disabled={view.locked}
            className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40" style={{ color: "var(--text)" }}
            title={view.locked ? "Unlock to reseed" : ""}><RefreshCw className="w-3.5 h-3.5 text-primary-c" /> Reset & reseed</button>
        </div>
      </div>

      <div ref={bracketRef} className="p-2 rounded-xl" style={{ background: "var(--bg)" }}>
        {view.pool_count > 1 ? (
          <>
            {(view.playoff_format || category.playoff_format) !== "none" && (
            <div className="flex items-center gap-3 flex-wrap rounded-xl glow-border p-3 mb-6" style={{ background: "var(--surface)" }} data-testid="playoff-control">
              <Trophy className="w-4 h-4 text-primary-c" />
              <span className="text-sm font-bold">Championship playoffs</span>
              <span className="text-xs" style={{ color: "var(--text-2)" }}>Advance</span>
              <select data-testid="playoff-advance" value={advN} onChange={(e) => setAdvN(Number(e.target.value))} disabled={view.has_playoff}
                className="rounded-lg glow-border bg-surface px-2 py-1 text-sm outline-none disabled:opacity-50" style={{ color: "var(--text)" }}>
                <option value={1}>Winner only (1/pool)</option>
                <option value={2}>Top 2 per pool</option>
              </select>
              {(view.playoff_format || category.playoff_format || "single_elim") === "single_elim" && (
              <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: "var(--text-2)" }}>
                <input data-testid="playoff-3rd" type="checkbox" checked={playoff3rd} onChange={(e) => setPlayoff3rd(e.target.checked)} disabled={view.has_playoff} className="accent-lime-400" />
                3rd-place match
              </label>
              )}
              <button data-testid="generate-playoffs" onClick={genPlayoffs}
                className="btn-glow rounded-full px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ml-auto">
                <Trophy className="w-3.5 h-3.5" /> {view.has_playoff ? "Rebuild playoffs" : "Generate playoffs"}
              </button>
            </div>
            )}

            {view.pools.map((p) => {
              const sub = { ...view, matches: view.matches.filter((m) => (m.pool) === p.pool_index), standings: p.standings, champion_name: p.champion_name };
              return (
                <div key={p.pool_index} className="mb-8" data-testid={`bracket-pool-${p.pool_index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-display font-bold text-lg text-primary-c">{p.label}</h4>
                    {p.champion_name && <span className="text-xs glass rounded-full px-3 py-1 flex items-center gap-1.5" data-testid={`pool-champion-${p.pool_index}`}><Crown className="w-3.5 h-3.5" style={{ color: "#eab308" }} /> {p.champion_name}</span>}
                  </div>
                  {renderBody(sub)}
                </div>
              );
            })}

            {view.has_playoff && (
              <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--border)" }} data-testid="playoff-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-display font-bold text-lg flex items-center gap-2"><Trophy className="w-5 h-5" style={{ color: "#eab308" }} /> Playoffs — Overall title</h4>
                  {view.champion_name && <span className="text-xs glass rounded-full px-3 py-1 flex items-center gap-1.5" data-testid="playoff-champion"><Crown className="w-3.5 h-3.5" style={{ color: "#eab308" }} /> {view.champion_name}</span>}
                </div>
                {renderBody({ ...view, format: view.playoff_format || "single_elim", standings: view.playoff_standings, matches: view.matches.filter((m) => m.stage === "playoff") })}
              </div>
            )}
          </>
        ) : renderBody(view)}
      </div>

      <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Reset this bracket?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>All recorded results for {categoryLabel(category)} will be cleared and the bracket re-seeded.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regen-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="regen-confirm" onClick={(e) => { e.preventDefault(); regenerate(); }} className="bg-red-600 hover:bg-red-700 text-white">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
